import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Label,
  Modal,
  Segmented,
  Spinner,
  Textarea,
} from '@crm/ui';
import { Bot, Lock, Power, PowerOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type BotConfig, useBotConfig, useSaveBotConfig } from '../api';

type Rascunho = Partial<Omit<BotConfig, 'preview'>>;

/** O que o assistente faz por conta propria - nao ha menu sem isto. */
const FIXOS = [
  { label: 'Fazer um agendamento', hint: 'Escolhe servico, profissional, dia e hora' },
  { label: 'Reagendar', hint: 'Muda o dia e a hora de um horario ja marcado' },
  { label: 'Cancelar agendamento', hint: 'Desmarca o horario e devolve a vaga para a agenda' },
];

/** O texto no formato em que ele chega: ler no campo nao e a mesma coisa. */
function Balao({ texto }: { texto: string }) {
  return (
    <div className="rounded-card bg-muted/40 p-3">
      <div className="flex justify-start">
        <div className="relative max-w-[80%] rounded-2xl rounded-bl-sm bg-primary px-3 py-2 text-primary-foreground shadow-sm after:absolute after:bottom-0 after:left-[-5px] after:h-3 after:w-3 after:rotate-45 after:bg-primary after:content-['']">
          <p className="whitespace-pre-wrap break-words text-sm">{texto}</p>
        </div>
      </div>
    </div>
  );
}

export function BotConfigPage() {
  useDocumentTitle('Assistente do WhatsApp');

  const canWrite = useCan('settings', 'WRITE');
  const config = useBotConfig();
  const salvar = useSaveBotConfig();

  const [rascunho, setRascunho] = useState<Rascunho>({});
  const [avisandoFixo, setAvisandoFixo] = useState(false);

  useEffect(() => {
    if (!config.data) return;

    setRascunho({
      welcome: config.data.welcome,
      handoffReply: config.data.handoffReply,
      offHoursReply: config.data.offHoursReply,
    });
  }, [config.data]);

  if (config.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const alterado = (['welcome', 'handoffReply', 'offHoursReply'] as const).some(
    (campo) => rascunho[campo] !== config.data?.[campo],
  );

  return (
    <div className="space-y-5">
      {/* Em janela, e nao em linha solta: o aviso responde a um clique, e
          respondido de lado ele passava batido. */}
      {avisandoFixo ? (
        <Modal
          open
          size="small"
          title="Este item nao se desliga"
          onClose={() => setAvisandoFixo(false)}
          actions={[{ label: 'Entendi', onClick: () => setAvisandoFixo(false) }]}
        >
          <p className="text-sm">
            Marcar horario, reagendar e cancelar sao o que o assistente existe para fazer. Sem eles
            o cliente ficaria esperando por uma pessoa que talvez nao esteja online.
          </p>
        </Modal>
      ) : null}

      <PageHeader
        icon={<Bot />}
        title="Assistente do WhatsApp"
        description="Responde o cliente na hora e conduz ate o horario marcado."
      />

      {salvar.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(salvar.error)}</p>
      ) : null}

      {/* Os dois interruptores lado a lado no desktop: sozinhos em linha
          inteira, o controle ficava a mil pixels do proprio rotulo. */}
      <Card variant="flush">
        <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 py-4">
          <Label>Status do assistente:</Label>
          <Segmented
            label="Status do assistente"
            options={[
              { value: 'on', label: 'Ligado', icon: Power },
              {
                value: 'off',
                label: 'Desligado',
                icon: PowerOff,
                hint: 'Toda mensagem cai direto para a equipe.',
              },
            ]}
            value={config.data?.enabled ? 'on' : 'off'}
            disabled={!canWrite || salvar.isPending}
            onChange={(estado) => salvar.mutate({ enabled: estado === 'on' })}
          />
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="space-y-4 py-4">
          <Label>Itens do menu inicial</Label>

          {/* Marcar, reagendar e cancelar nao se desligam: sao a razao de o
              assistente existir, e um menu sem eles deixaria o cliente
              esperando por uma pessoa que talvez nao esteja online. */}
          {FIXOS.map((item) => (
            // A caixa desabilitada nao responde a clique, entao quem escuta e
            // a linha inteira: sem isso, insistir nela nao dizia nada.
            <button
              key={item.label}
              type="button"
              className="block w-full text-left"
              onClick={() => setAvisandoFixo(true)}
            >
              <Checkbox
                label={
                  <span className="inline-flex items-center gap-1.5">
                    {item.label}
                    <Lock className="h-3 w-3 text-muted-foreground" />
                  </span>
                }
                hint={item.hint}
                checked
                locked
                readOnly
              />
            </button>
          ))}

          <Checkbox
            label="Consultar preco de servico"
            hint="Permite que o cliente consulte o preco dos servicos"
            disabled={!canWrite || salvar.isPending}
            checked={config.data?.priceMenu ?? true}
            onChange={(input) => salvar.mutate({ priceMenu: input.target.checked })}
          />

          <Checkbox
            label="Falar com um atendente"
            hint="Desligado, o assistente conduz sozinho e nao oferece a saida."
            disabled={!canWrite || salvar.isPending}
            checked={config.data?.humanMenu ?? true}
            onChange={(input) => salvar.mutate({ humanMenu: input.target.checked })}
          />
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="space-y-4 py-4">
          <Label>Mensagens padrao</Label>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="bot-welcome">Boas-vindas</Label>
              <Textarea
                id="bot-welcome"
                autoGrow
                rows={2}
                disabled={!canWrite}
                value={rascunho.welcome ?? ''}
                onChange={(input) => setRascunho({ ...rascunho, welcome: input.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>O que o cliente recebe</Label>
              <Balao texto={config.data?.preview ?? ''} />
            </div>
          </div>

          {/* A resposta so existe se a opcao existir - fora dai era um campo
              pedindo o texto de uma porta que ninguem ve. */}
          {config.data?.humanMenu ? (
            <div className="space-y-2">
              <Label htmlFor="bot-handoff">Quando o cliente pede uma pessoa</Label>
              <Textarea
                id="bot-handoff"
                autoGrow
                rows={2}
                disabled={!canWrite}
                value={rascunho.handoffReply ?? ''}
                onChange={(input) => setRascunho({ ...rascunho, handoffReply: input.target.value })}
              />
            </div>
          ) : null}

          {canWrite ? (
            <div className="flex justify-end">
              <Button
                onClick={() => salvar.mutate(rascunho)}
                disabled={!alterado || salvar.isPending}
              >
                {salvar.isPending ? <Spinner /> : null}
                Salvar alteracoes
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
